import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { TabView, TabPanel } from "primereact/tabview";
import { ProgressSpinner } from "primereact/progressspinner";
import { Card } from "primereact/card";
import StatCard from "./StatCard";
import {
  getTeacherDashboard,
  getTeacherSchedule,
  getTeacherAssignmentsList,
} from "../../api/teacherDashboardApi";
import { useSchoolFeatures } from "../../common/utils/schoolSettings";
import { effectiveSlotTimes } from "../../common/utils/classTiming";

// ─── helpers ──────────────────────────────────────────────────────────────────

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = {
  mon: "Monday", tue: "Tuesday", wed: "Wednesday",
  thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday",
};

const formatTime = (t) => {
  if (!t) return "—";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${h12}:${mm} ${ampm}`;
};

// ─── Overview tab (existing dashboard content) ────────────────────────────────

function OverviewTab({ data, navigate }) {
  const attendanceTaken = (data?.todayAttendance || []).filter((r) => r.taken).length;
  const attendancePending = (data?.todayAttendance || []).length - attendanceTaken;

  const attendanceStatusBody = (row) =>
    row.taken ? (
      <Tag severity="success" value="Taken" />
    ) : (
      <Tag severity="warning" value="Pending" icon="pi pi-exclamation-triangle" />
    );

  const actionBody = (row) =>
    !row.taken ? (
      <a
        href="#"
        className="text-primary text-sm"
        onClick={(e) => {
          e.preventDefault();
          navigate(
            `/index/attendance-form?gradeId=${row.gradeId}&subjectId=${row.subjectId}&sessionId=${row.sessionId}`
          );
        }}
      >
        Take Now
      </a>
    ) : null;

  return (
    <div>
      <div className="grid mb-4">
        <div className="col-12 md:col-3">
          <StatCard
            title="My Classes"
            value={data?.assignedClasses?.length ?? 0}
            icon="pi-th-large"
            color="#6366f1"
            subtitle="Grade-subject assignments"
          />
        </div>
        <div className="col-12 md:col-3">
          <StatCard
            title="Attendance Today"
            value={`${attendanceTaken} / ${(data?.todayAttendance || []).length}`}
            icon="pi-check-square"
            color={attendancePending > 0 ? "#f59e0b" : "#10b981"}
            subtitle={attendancePending > 0 ? `${attendancePending} pending` : "All taken"}
          />
        </div>
        <div className="col-12 md:col-3">
          <StatCard
            title="Pending Grading"
            value={data?.pendingGrading ?? 0}
            icon="pi-pencil"
            color="#ef4444"
            subtitle="Submissions awaiting grade"
            onClick={data?.pendingGrading > 0 ? () => navigate("/index/assignments") : undefined}
          />
        </div>
        <div className="col-12 md:col-3">
          <StatCard
            title="Due This Week"
            value={data?.upcomingDue ?? 0}
            icon="pi-calendar-clock"
            color="#3b82f6"
            subtitle="Published assignments due soon"
          />
        </div>
      </div>

      <Card className="mb-4">
        <h3 className="mt-0 mb-3 text-base font-semibold">Today's Attendance</h3>
        {(data?.todayAttendance || []).length === 0 ? (
          <p className="text-color-secondary m-0">No classes assigned.</p>
        ) : (
          <DataTable value={data.todayAttendance} size="small" showGridlines={false} stripedRows>
            <Column field="grade" header="Grade" sortable />
            <Column field="subject" header="Subject" sortable />
            <Column field="enrolledCount" header="Students" style={{ width: "6rem", textAlign: "center" }} />
            <Column header="Status" body={attendanceStatusBody} style={{ width: "8rem" }} />
            <Column header="" body={actionBody} style={{ width: "7rem" }} />
          </DataTable>
        )}
      </Card>

      <Card>
        <h3 className="mt-0 mb-3 text-base font-semibold">My Classes</h3>
        {(data?.assignedClasses || []).length === 0 ? (
          <p className="text-color-secondary m-0">No classes assigned yet.</p>
        ) : (
          <DataTable value={data.assignedClasses} size="small" stripedRows>
            <Column field="grade" header="Grade" sortable />
            <Column field="subject" header="Subject" sortable />
            <Column field="session" header="Session" />
          </DataTable>
        )}
      </Card>
    </div>
  );
}

// ─── Schedule tab ─────────────────────────────────────────────────────────────

function ScheduleTab({ teacherId }) {
  const features = useSchoolFeatures();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teacherId) { setLoading(false); return; }
    getTeacherSchedule(teacherId)
      .then((data) => {
        const items = Array.isArray(data) ? data : (data?.items || []);
        setSchedules(items);
      })
      .catch(() => setSchedules([]))
      .finally(() => setLoading(false));
  }, [teacherId]);

  if (loading) return <div className="flex justify-content-center p-5"><ProgressSpinner /></div>;
  if (!teacherId) return <p className="text-color-secondary text-center p-4">Teacher profile not found.</p>;
  if (schedules.length === 0) return <p className="text-color-secondary text-center p-4">No schedule assigned yet.</p>;

  const byDay = DAY_ORDER.reduce((acc, day) => {
    const rows = schedules
      .filter((s) => s.day_of_week === day)
      .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    if (rows.length) acc[day] = rows;
    return acc;
  }, {});

  if (Object.keys(byDay).length === 0)
    return <p className="text-color-secondary text-center p-4">No scheduled classes.</p>;

  return (
    <div>
      <div className="flex align-items-center gap-2 mb-3">
        <i className="pi pi-calendar text-primary" />
        <span className="font-semibold">My Weekly Schedule</span>
      </div>
      {Object.entries(byDay).map(([day, rows]) => (
        <div key={day} className="mb-4">
          <div className="mb-2">
            <Tag value={DAY_LABELS[day]} severity="info" />
          </div>
          <DataTable value={rows} size="small" stripedRows className="border-1 surface-border border-round">
            <Column
              header="Time"
              body={(r) => {
                const t = effectiveSlotTimes(r, features);
                return `${formatTime(t.start_time)} – ${formatTime(t.end_time)}`;
              }}
              style={{ width: "11rem" }}
            />
            <Column header="Subject" body={(r) => r.subject?.name || "—"} />
            <Column header="Grade" body={(r) => r.grade?.name || "—"} />
            <Column header="Classroom" body={(r) => r.classroom?.name || "—"} />
          </DataTable>
        </div>
      ))}
    </div>
  );
}

// ─── Assignments tab ──────────────────────────────────────────────────────────

function AssignmentsTab({ teacherId, navigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teacherId) { setLoading(false); return; }
    getTeacherAssignmentsList(teacherId)
      .then((data) => setItems(data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [teacherId]);

  if (loading) return <div className="flex justify-content-center p-5"><ProgressSpinner /></div>;
  if (items.length === 0) return <p className="text-color-secondary text-center p-4">No assignments yet.</p>;

  const statusSeverity = { draft: "secondary", published: "success", closed: "warning" };

  return (
    <DataTable
      value={items}
      size="small"
      stripedRows
      paginator
      rows={10}
      rowsPerPageOptions={[10, 20]}
      sortField="createdAt"
      sortOrder={-1}
    >
      <Column field="title" header="Title" sortable />
      <Column header="Grade" body={(r) => r.grade?.name || "—"} />
      <Column header="Subject" body={(r) => r.subject?.name || "—"} />
      <Column
        header="Due"
        body={(r) => r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}
        sortable
        field="due_date"
      />
      <Column
        header="Status"
        body={(r) => (
          <Tag
            value={r.status}
            severity={statusSeverity[r.status] || "info"}
          />
        )}
        style={{ width: "8rem" }}
      />
    </DataTable>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const tabMap = ["overview", "schedule", "assignments"];

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [tabIndex, setTabIndex] = useState(
    initialTab && tabMap.includes(initialTab) ? tabMap.indexOf(initialTab) : 0
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTeacherDashboard()
      .then(setData)
      .catch(() =>
        setData({ teacherId: null, assignedClasses: [], todayAttendance: [], pendingGrading: 0, upcomingDue: 0 })
      )
      .finally(() => setLoading(false));
  }, []);

  const handleTabChange = (e) => {
    setTabIndex(e.index);
    setSearchParams({ tab: tabMap[e.index] });
  };

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="mb-4 mt-0">My Dashboard</h2>
      <TabView activeIndex={tabIndex} onTabChange={handleTabChange}>
        <TabPanel header="Overview" leftIcon="pi pi-th-large mr-2">
          <OverviewTab data={data} navigate={navigate} />
        </TabPanel>
        <TabPanel header="My Schedule" leftIcon="pi pi-calendar mr-2">
          <ScheduleTab teacherId={data?.teacherId} />
        </TabPanel>
        <TabPanel header="Assignments" leftIcon="pi pi-file-edit mr-2">
          <AssignmentsTab teacherId={data?.teacherId} navigate={navigate} />
        </TabPanel>
      </TabView>
    </div>
  );
}
